import React, { useState } from "react";
import { getLocale } from "egov-ui-kit/utils/localStorageUtils";
import { formatINR,sumField,safeArray } from "egov-ui-kit/utils/commons";

const LEVELS = [
  { key: "ulbs",         label: "ULB Name",         childKey: "zones" },
  { key: "zones",        label: "Zone Name",        childKey: "departments" },
  { key: "departments",  label: "Department",       childKey: "fund",  extraColumn: { label: "Department Code", field: "deptCode" } },
  { key: "fund",         label: "Fund",              childKey: "accountHeads" },
  { key: "accountHeads", label: "Account Name", childKey: null,   extraColumn: { label: "Account Code", field: "accountHeadCode" } },
];

const normalizeFundList = (rawList) =>
  safeArray(rawList).map((f, idx) => ({
    id: f.fundName || `fund_${idx}`,
    name: f.fundName,
    totalExpenditure: f.totalAmount,
    taxHeadDetails: safeArray(f.taxHeadDetails),
  }));

// No grouping/merging: every taxHeadDetails record is shown as its own row,
// even if multiple records share the same glCode. The "Total" row at the
// bottom still sums all of them together (duplicates included).
const groupAccountHeads = (taxHeadDetails) =>
  safeArray(taxHeadDetails).map((r, idx) => ({
    id: `${r.glCode}_${idx}`, // unique key even when glCode repeats
    name: r.taxHeadName,
    accountHeadCode: r.glCode,
    totalExpenditure: Number(r.amount) || 0,
  }));

const TotalExpenditureTable = ({
  expenditureDetails,
  expenditureFundDetails,
  fetchExpenditureFundDetails,
  ulbType,
  fromDate,
  toDate,
}) => {
  const tableData = expenditureDetails && expenditureDetails;
  const totalExpSum = safeArray(tableData.ulbs).reduce(
    (acc, ulb) => acc + (Number(ulb.totalExpenditure) || 0),
    0
  );

  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null); // needed for fund payload's zoneName

  // ── Derived ────────────────────────────────
  const levelIndex = breadcrumb.length;
  const levelConfig = LEVELS[levelIndex];
  const canDrillDown = levelConfig.childKey !== null; // false only at Account Head level (last)
  const isRootLevel = levelIndex === 0;
  const isDepartmentLevel = levelConfig.key === "departments";
  const isFundLevel = levelConfig.key === "fund";
  const isAccountHeadLevel = levelConfig.key === "accountHeads";
  const currentRows = (function () {
    if (isRootLevel) {
      return safeArray(tableData[LEVELS[0].key]);
    }
    var currentKey = LEVELS[levelIndex].key;

    if (currentKey === "fund") {
      return normalizeFundList(expenditureFundDetails);
    }
    if (currentKey === "accountHeads") {
      var parentFund = breadcrumb[levelIndex - 1].item; // the fund row that was clicked
      return groupAccountHeads(parentFund.taxHeadDetails);
    }

    var parentItem = breadcrumb[levelIndex - 1].item;
    return safeArray(parentItem[currentKey]);
  })();

  const currentTitle = isRootLevel ? "ULB Wise Expenditure" : breadcrumb[levelIndex - 1].item.name;
  const currentTotal = sumField(currentRows, "totalExpenditure");

  const handleDrillDown = (item) => {
    if (!canDrillDown) return; // Account Head level — no further drill-down

    if (levelConfig.key === "zones") {
      setSelectedZone(item);
    }
    if (isDepartmentLevel) {
      const payload = {
        financialYear: tableData.financialYear,
        ulbName: breadcrumb[0] ? breadcrumb[0].item.tenantId : null,
        zoneName: selectedZone ? selectedZone.name : null,
        deptCode: item.deptCode || null,
        ulbType: ulbType,
        fromDate: fromDate,
        toDate: toDate,
        locale: getLocale(),
      };
      fetchExpenditureFundDetails(payload);
    }

    setBreadcrumb(function (prev) {
      return prev.concat([{ levelIndex: levelIndex, item: item }]);
    });
  };

  const handleBack = () => {
    setBreadcrumb(function (prev) {
      var newBreadcrumb = prev.slice(0, -1);
      if (newBreadcrumb.length < 2) setSelectedZone(null);
      return newBreadcrumb;
    });
  };

  const handleBreadcrumbClick = (toIndex) => {
    setBreadcrumb(function (prev) {
      var newBreadcrumb = prev.slice(0, toIndex);
      if (newBreadcrumb.length < 2) setSelectedZone(null);
      return newBreadcrumb;
    });
  };

  const emptyMessage = isFundLevel ? "Loading fund details..." : "No Record Found";

  // ── Render ─────────────────────────────────
  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          {isRootLevel ? (
            <div className="icon-wrapper">
              <i className="material-icons">description</i>
            </div>
          ) : (
            <button className="btn btn-secondary back-button" onClick={handleBack}>
              Back
            </button>
          )}
          <div>
            <h2>{currentTitle}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {tableData.financialYear}</span>
          <span>|</span>
          <span>ULB : {tableData.ulbName}</span>
        </div>
      </div>

      {/* BREADCRUMB */}
      {!isRootLevel && (
        <div className="breadcrumb-bar">
          <span className="bc-link" onClick={() => handleBreadcrumbClick(0)} role="button" tabIndex={0}>
            ULB Wise Expenditure
          </span>
          {breadcrumb.map(function (bc, idx) {
            var isLast = idx === breadcrumb.length - 1;
            return (
              <React.Fragment key={bc.item.id}>
                <span className="bc-sep"> › </span>
                {isLast ? (
                  <span className="bc-current">{bc.item.name}</span>
                ) : (
                  <span className="bc-link" onClick={() => handleBreadcrumbClick(idx + 1)} role="button" tabIndex={0}>
                    {bc.item.name}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* SINGLE TABLE — ULB / ZONE / DEPARTMENT / FUND / ACCOUNT HEAD LEVEL */}
      <div className={`table-responsive ${levelConfig.key === "ulbs" || levelConfig.key === "zones" || levelConfig.key === "departments" ? "scrolling-table" : ""}`}>
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>{levelConfig.label}</th>
              {levelConfig.extraColumn && <th>{levelConfig.extraColumn.label}</th>}
              <th>Total Expenditure</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <React.Fragment>
                {currentRows.map(function (item) {
                  return (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      {levelConfig.extraColumn && <td>{item[levelConfig.extraColumn.field]}</td>}
                      <td>
                        {canDrillDown ? (
                          <a
                            href="#"
                            className="link-cell"
                            onClick={function (e) {
                              e.preventDefault();
                              handleDrillDown(item);
                            }}
                          >
                            {formatINR(item.totalExpenditure)}
                          </a>
                        ) : (
                          formatINR(item.totalExpenditure) // Account Head level — plain text, not clickable
                        )}
                      </td>
                    </tr>
                  );
                })}
              
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan={levelConfig.extraColumn ? 3 : 2} style={{ textAlign: "center" }}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
         {currentRows.length > 0 && (
              <tfoot className="tfoot-bottom">
                <tr className="total-row">
                  <td colSpan={levelConfig.extraColumn ? 2 : 1} style={{ textAlign: "left" }}>Total</td>
                  <td>{formatINR(currentTotal)}</td>
                </tr>
              </tfoot>
            )}
        </table>
      </div>
    </div>
  );
};

export default TotalExpenditureTable;


// WEBPACK FOOTER //
// src/modules/employee/Home/TotalExpenditureTable.js